import { useEffect } from "react";
import { pick } from "lodash";
import type { FieldValues } from "react-hook-form";
import { useWatch } from "react-hook-form";
import { useLocalStorage } from "./useLocalStorage";

export interface UsePersistFormFieldsProps {
  cacheKey?: string;
  enabled?: boolean;
  fields?: string[];
}

export const usePersistFormFields = ({
  cacheKey,
  fields = [],
  enabled = true,
}: UsePersistFormFieldsProps) => {
  const fieldValues: FieldValues = useWatch<FieldValues>();
  const formFieldsToPersist = pick(fieldValues, fields);
  const { setValue: setPersistedValue } = useLocalStorage(cacheKey);

  useEffect(() => {
    if (enabled && cacheKey) {
      setPersistedValue(formFieldsToPersist);
    }
  }, [formFieldsToPersist, cacheKey, enabled, setPersistedValue]);
};

export interface UsePersistedFormFieldValuesProps {
  cacheKey?: string;
}

export const usePersistedFormFieldValues = <T = FieldValues>({
  cacheKey,
}: UsePersistedFormFieldValuesProps) => {
  if (!cacheKey) throw new Error("CacheKey is required");
  const { getValue: getPersistedFormFieldValues, resetValue: resetValues } =
    useLocalStorage<T>(cacheKey);

  return { getPersistedFormFieldValues, resetValues };
};
